/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { PlungerSpec, TableInfoSpec } from "../../../shared/contracts";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

export class PlungerData extends ItemData {
  public center: Vertex2D;
  public width: number = 25;
  public height: number = 20;
  public zAdjust: number = this.height * 4;
  /** Length of the plunger stroke in table units. */
  public stroke: number = 80;
  public speedPull: number = 0.5;
  public speedFire: number = 80;
  public mechStrength: number = 85;
  public parkPosition: number = 0.5 / 3.0;
  public scatterVelocity: number = 0;
  public momentumXfer: number = 1;
  public autoPlunger: boolean = false;
  public szMaterial?: string;
  public isVisible: boolean = true;
  public szSurface?: string;

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: PlungerSpec, _info?: TableInfoSpec): PlungerData {
    const data = new PlungerData(spec.name, toVertex2D(spec.center));
    data.width = spec.width;
    data.height = spec.height;
    data.zAdjust = spec.zAdjust ?? spec.height * 4;
    data.stroke = spec.stroke ?? data.stroke;
    data.speedPull = spec.speedPull ?? data.speedPull;
    data.speedFire = spec.speedFire ?? data.speedFire;
    data.mechStrength = spec.mechStrength ?? data.mechStrength;
    data.parkPosition = spec.parkPosition ?? data.parkPosition;
    data.scatterVelocity = spec.scatterVelocity ?? data.scatterVelocity;
    data.momentumXfer = spec.momentumXfer ?? data.momentumXfer;
    data.autoPlunger = spec.autoPlunger ?? false;
    data.szMaterial = spec.szMaterial;
    data.isVisible = spec.isVisible ?? true;
    data.szSurface = spec.szSurface;
    return data;
  }
}
